import { EntityType } from '@/components/entities/entityType';
import { useApiContext } from '@/contexts/apiContext';
import { ExecutionStep, ExecutionStepResult, PipelineExecutionState } from '@/interfaces/pipelineExecution.interface';
import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AssistantChat, { AssistantChatHandle, DetectedEntityInfo } from './assistantChat/assistantChat';
import ChantAssistantHeader from './chatAssistantHeader';
import EntityContextPanel from './entityContextPanel/entityContextPanel';
import { SuggestionItem } from './entityContextPanel/emptyContextPanel';
import { HistoryEntry, RightPanelContext } from './entityContextPanel/types';

const buildPipelineEntitySummary = (plan: PipelineExecutionState, step?: ExecutionStep | null): string => {
  const stepList = (plan.steps || [])
    .map(
      (s, idx) =>
        `  ${idx + 1}. Step ${idx + 1} (stepId: "${s.id}"): "${s.name || s.entityName || s.entityDefName || s.entityDefId}" (entityType: "${s.entityType}", entityDefId: "${s.entityDefId}", entityId: "${s.entityId}") - Status: ${s.status}`,
    )
    .join('\n');

  let summary = `Pipeline (ID: "${plan.id || plan.pipelineInstanceId}", Name: "${plan.pipelineName}", Status: "${plan.status}"):\nSteps:\n${stepList}`;
  if (step) {
    summary += `\nCurrently Selected Step: Step "${step.name || step.entityName}" (stepId: "${step.id}", entityType: "${step.entityType}", entityDefId: "${step.entityDefId}", entityId: "${step.entityId}", Status: "${step.status}")`;
  }
  return summary;
};

const ChatAssistantPage: React.FC = () => {
  const apiContext = useApiContext();

  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [pipelineExecutionState, setPipelineExecutionState] = useState<PipelineExecutionState>();
  const [selectedStep, setSelectedStep] = useState<ExecutionStep | null>(null);
  const [executingStepId, setExecutingStepId] = useState<string | null>(null);
  const [stepExecutionResponse, setStepExecutionResponse] = useState<ExecutionStepResult | null>(null);
  const [suppressEvents, setSuppressEvents] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const chatRef = useRef<AssistantChatHandle | null>(null);

  // Saved conversation history
  const [conversations, setConversations] = useState<any[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  // Right panel context — tracks what's displayed for LLM prompt injection
  const [rightPanelContext, setRightPanelContext] = useState<RightPanelContext>({ type: 'empty' });

  // Load saved conversations on mount and auto-load most recent chat
  useEffect(() => {
    setConversationsLoading(true);
    apiContext
      .getAssistantConversations()
      .then((convs) => {
        setConversations(convs || []);
        if (convs && convs.length > 0) {
          setActiveConversationId(convs[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setConversationsLoading(false));
  }, [apiContext]);

  const refreshConversations = useCallback(async () => {
    try {
      const convs = await apiContext.getAssistantConversations();
      setConversations(convs || []);
    } catch {}
  }, [apiContext]);

  const handleSelectConversation = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
  }, []);

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        await apiContext.deleteAssistantConversation(conversationId);
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        if (activeConversationId === conversationId) {
          setActiveConversationId(null);
          chatRef.current?.startNewConversation();
        }
      } catch {}
    },
    [apiContext, activeConversationId],
  );

  // Context history for time travel navigation
  const [historyList, setHistoryList] = useState<HistoryEntry[]>([
    {
      id: 'init',
      title: 'Workspace Suggestions',
      type: 'empty',
      timestamp: new Date(),
      context: { type: 'empty' },
    },
  ]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(0);

  const pushHistory = useCallback(
    (context: RightPanelContext, title: string, plan?: PipelineExecutionState) => {
      const entry: HistoryEntry = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        title,
        type: context.type,
        timestamp: new Date(),
        context,
        pipelineExecutionState: plan,
      };
      setHistoryList((prev) => {
        const base = prev.slice(0, currentHistoryIndex + 1);
        return [...base, entry];
      });
      setCurrentHistoryIndex((prev) => prev + 1);
    },
    [currentHistoryIndex],
  );

  const handleHistoryJump = useCallback(
    (index: number) => {
      if (index >= 0 && index < historyList.length) {
        setCurrentHistoryIndex(index);
        const entry = historyList[index];
        setRightPanelContext(entry.context);
        if (entry.pipelineExecutionState) {
          setPipelineExecutionState(entry.pipelineExecutionState);
          setPipelineId(entry.pipelineExecutionState.id);
          if (entry.pipelineExecutionState.steps && entry.pipelineExecutionState.steps.length > 0) {
            setSelectedStep(entry.pipelineExecutionState.steps[0]);
          }
        } else {
          setPipelineExecutionState(undefined);
          setPipelineId(null);
          setSelectedStep(null);
        }
      }
    },
    [historyList],
  );

  const handleHistoryBack = useCallback(() => {
    if (currentHistoryIndex > 0) {
      handleHistoryJump(currentHistoryIndex - 1);
    }
  }, [currentHistoryIndex, handleHistoryJump]);

  const handleHistoryForward = useCallback(() => {
    if (currentHistoryIndex < historyList.length - 1) {
      handleHistoryJump(currentHistoryIndex + 1);
    }
  }, [currentHistoryIndex, historyList.length, handleHistoryJump]);

  const loadedPlanIdRef = useRef<string | null>(null);

  const reloadPipelineExecutionPlan = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const plan = await apiContext.getPipelineExecutionPlan(pipelineId);
      setPipelineExecutionState(plan);
      // Keep selectedStep in sync with updated plan
      if (selectedStep) {
        const match = plan.steps?.find(
          (s: ExecutionStep) =>
            (s.id !== undefined && s.id === selectedStep.id) || (s.entityId && s.entityId === selectedStep.entityId),
        );
        if (match) setSelectedStep(match);
      }
      // If a step was executing, check if it has finished
      setExecutingStepId((currentExecutingId) => {
        if (!currentExecutingId) return null;
        const execStep = plan.steps?.find(
          (s: ExecutionStep) => s.id === currentExecutingId || s.entityId === currentExecutingId,
        );
        if (execStep && execStep.status !== 'running' && (execStep.status as string) !== 'processing') {
          setStepExecutionResponse((prev) => (prev?.status === 'running' ? null : prev));
          return null;
        }
        return currentExecutingId;
      });
    } catch (e: any) {
      setError('Failed to reload pipeline execution plan.');
    }
  }, [pipelineId, selectedStep, apiContext]);

  const handlePipelineSelect = useCallback(
    async (selectedPipelineId: string, fallbackData?: any) => {
      setPipelineId(selectedPipelineId);
      setSelectedStep(null);
      setStepExecutionResponse(null);
      loadedPlanIdRef.current = selectedPipelineId;
      try {
        const plan = await apiContext.getPipelineExecutionPlan(selectedPipelineId);
        setPipelineExecutionState(plan);
        let initialStep = plan?.steps?.[0] || null;
        if (plan?.steps?.length > 0) {
          const pendingIdx = plan.steps.findIndex((s: ExecutionStep) => s.status === 'pending');
          const targetIdx = pendingIdx !== -1 ? pendingIdx : 0;
          initialStep = plan.steps[targetIdx];
          setSelectedStep(initialStep);
        }
        const newCtx: RightPanelContext = {
          type: 'pipeline',
          pipelineId: selectedPipelineId,
          pipelineName: plan.pipelineName,
          entitySummary: buildPipelineEntitySummary(plan, initialStep),
        };
        setRightPanelContext(newCtx);
        pushHistory(newCtx, `Pipeline: ${plan.pipelineName || selectedPipelineId}`, plan);
      } catch {
        if (fallbackData) {
          const rawSteps = fallbackData.steps || fallbackData.entities || fallbackData.items || [];
          const convertedSteps: ExecutionStep[] = rawSteps.map((e: any, idx: number) => ({
            id: String(e.id ?? `step_${idx + 1}`),
            name: e.name || `${e.entityType || 'Component'} #${e.id || idx + 1}`,
            entityType: e.entityType || EntityType.agentInstance,
            entityDefId: e.entityDefId || String(e.id || ''),
            entityId: String(e.id ?? `step_${idx + 1}`),
            entityName: e.name || `${e.entityType || 'Component'} #${e.id || idx + 1}`,
            entityDefName: e.defName || e.name || `${e.entityType || 'Component'} #${e.id || idx + 1}`,
            status: 'pending',
            upstreamEntities: [],
          }));

          const plan: PipelineExecutionState = {
            id: selectedPipelineId,
            pipelineInstanceId: selectedPipelineId,
            pipelineName: fallbackData.name || fallbackData.pipelineName || selectedPipelineId,
            status: fallbackData.status || 'pending',
            steps: convertedSteps,
            currentStepId: convertedSteps[0]?.id || '',
          };
          setPipelineExecutionState(plan);
          const newCtx: RightPanelContext = {
            type: 'pipeline',
            pipelineId: selectedPipelineId,
            pipelineName: plan.pipelineName,
            entitySummary: buildPipelineEntitySummary(plan, convertedSteps[0]),
          };
          setRightPanelContext(newCtx);
          pushHistory(newCtx, `Pipeline: ${plan.pipelineName}`, plan);
          if (plan.steps?.length > 0) {
            setSelectedStep(plan.steps[0]);
          }
        } else {
          setError('Failed to load pipeline execution plan.');
        }
      }
    },
    [apiContext, pushHistory],
  );

  // Auto-dismiss completed step execution response after 5 seconds (not while running/in-progress)
  useEffect(() => {
    if (stepExecutionResponse) {
      const isFailed = stepExecutionResponse.status === 'failed' || Boolean(stepExecutionResponse.error);
      const isRunning = stepExecutionResponse.status === 'running';
      if (!isFailed && !isRunning) {
        const timer = setTimeout(() => {
          setStepExecutionResponse(null);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [stepExecutionResponse]);

  // Update right panel context when selected step changes
  useEffect(() => {
    if (pipelineExecutionState) {
      setRightPanelContext((prev) => ({
        ...prev,
        selectedStepId: selectedStep?.id,
        entitySummary: buildPipelineEntitySummary(pipelineExecutionState, selectedStep),
      }));
    }
  }, [selectedStep, pipelineExecutionState]);

  const handleExecuteStep = useCallback(
    async (step: ExecutionStep, options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean }) => {
      const pipelineId =
        pipelineExecutionState?.id || pipelineExecutionState?.pipelineInstanceId || rightPanelContext.pipelineId || '';
      if (!pipelineId) return;
      setExecutingStepId(step.id);
      setStepExecutionResponse(null);
      let isTimeout = false;
      try {
        const effectiveSuppressEvents = options?.suppressEvents !== undefined ? options.suppressEvents : suppressEvents;
        const res = await apiContext.executePipelineStep(pipelineId, {
          action: 'execute',
          stepId: step.id,
          entityType: step.entityType,
          entityDefId: step.entityDefId,
          entityId: step.entityId,
          reingest: options?.reingest,
          suppressEvents: effectiveSuppressEvents,
        });
        setStepExecutionResponse(res);
        await reloadPipelineExecutionPlan();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to execute step. Please check service connectivity and configurations.';
        isTimeout = String(msg).toLowerCase().includes('timeout') || String(msg).toLowerCase().includes('timed out');
        if (isTimeout) {
          setStepExecutionResponse({
            stepId: step.id,
            status: 'running',
            message:
              'Request timed out waiting for all items, but processing is continuing in the background. Live monitoring active...',
          });
        } else {
          setStepExecutionResponse({ stepId: step.id, status: 'failed', error: msg });
        }
      } finally {
        if (!isTimeout) {
          setExecutingStepId(null);
        }
      }
    },
    [pipelineExecutionState, rightPanelContext.pipelineId, suppressEvents, apiContext, reloadPipelineExecutionPlan],
  );

  const handleRestartFromPoint = useCallback(
    async (step: ExecutionStep, itemId?: number | string) => {
      const pipelineId =
        pipelineExecutionState?.id || pipelineExecutionState?.pipelineInstanceId || rightPanelContext.pipelineId || '';
      if (!pipelineId) return;
      setExecutingStepId(step.id);
      setStepExecutionResponse(null);
      let isTimeout = false;
      try {
        const isDataSource = step.entityType === EntityType.dataSourceInstance;
        const res = await apiContext.executePipelineStep(pipelineId, {
          action: 'restart_from',
          stepId: step.id,
          entityType: step.entityType,
          entityDefId: step.entityDefId,
          entityId: step.entityId,
          dataSourceDefId: isDataSource ? step.entityDefId : undefined,
          dataSourceInstanceId: isDataSource ? step.entityId : undefined,
          dataSourceItemId: isDataSource && itemId !== undefined ? String(itemId) : undefined,
          reingest: true,
        });
        setStepExecutionResponse(res);
        await reloadPipelineExecutionPlan();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to restart pipeline. Please check service connectivity and configurations.';
        isTimeout = String(msg).toLowerCase().includes('timeout') || String(msg).toLowerCase().includes('timed out');
        if (isTimeout) {
          setStepExecutionResponse({
            stepId: step.id,
            status: 'running',
            message:
              'Restart request timed out waiting, but execution is continuing in background. Live monitoring active...',
          });
        } else {
          setStepExecutionResponse({ stepId: step.id, status: 'failed', error: msg });
        }
      } finally {
        if (!isTimeout) {
          setExecutingStepId(null);
        }
      }
    },
    [pipelineExecutionState, rightPanelContext.pipelineId, apiContext, reloadPipelineExecutionPlan],
  );

  // Handle entity detection from assistant chat tool calls and responses
  const handleEntityDetected = useCallback(
    async (entityInfo: DetectedEntityInfo) => {
      if (entityInfo.type === 'pipeline_action') {
        const actionData = entityInfo.data;
        if (actionData) {
          const errMsg = String(actionData.error || '').toLowerCase();
          const isTimeout =
            errMsg.includes('timeout') || errMsg.includes('timed out') || errMsg.includes('econnaborted');

          const isFailed =
            !isTimeout &&
            (Boolean(actionData.isError) ||
              actionData.status === 'failed' ||
              Boolean(actionData.error) ||
              actionData.success === false);

          const targetStepId = actionData.stepId || selectedStep?.id || '';

          if (isTimeout) {
            setStepExecutionResponse({
              stepId: targetStepId,
              status: 'running',
              message:
                'Step execution initiated in background (request timed out waiting for batch). Live monitoring active.',
            });
            if (targetStepId) {
              setExecutingStepId(targetStepId);
            }
          } else if (isFailed) {
            setStepExecutionResponse({
              stepId: targetStepId,
              status: 'failed',
              error: actionData.error || 'Step execution failed',
            });
            setExecutingStepId(null);
          } else if (actionData.status === 'running') {
            setStepExecutionResponse({
              stepId: targetStepId,
              status: 'running',
              message: actionData.message || 'Step execution is currently running...',
            });
            if (targetStepId) {
              setExecutingStepId(targetStepId);
            }
          } else if (actionData.status === 'completed' || actionData.success) {
            setStepExecutionResponse({
              stepId: targetStepId,
              status: 'completed',
              message: actionData.message || 'Step executed successfully',
            });
            setExecutingStepId(null);
          }

          if (actionData.stepId && pipelineExecutionState?.steps) {
            const targetStep = pipelineExecutionState.steps.find((s) => s.id === actionData.stepId);
            if (targetStep) {
              setSelectedStep(targetStep);
            }
          }
        }
        // Refresh the execution plan in place without resetting or switching views
        reloadPipelineExecutionPlan();
      } else if (
        (entityInfo.entityType === 'pipelineInstance' || entityInfo.entityType === 'pipelineBlueprint') &&
        entityInfo.id
      ) {
        handlePipelineSelect(entityInfo.id, entityInfo.data);
      } else if (entityInfo.type === 'entity_list' && entityInfo.items) {
        // If the user already has a pipeline active and the list was part of an action within it, maintain pipeline view
        if (pipelineExecutionState && pipelineId) {
          reloadPipelineExecutionPlan();
          return;
        }
        setPipelineExecutionState(undefined);
        setPipelineId(null);
        setSelectedStep(null);
        const newCtx: RightPanelContext = {
          type: 'entity_list',
          entityList: {
            title: entityInfo.title || 'Entities',
            entityType: entityInfo.entityType as any,
            items: entityInfo.items,
          },
          entitySummary: `List of ${entityInfo.items.length} ${entityInfo.title || 'entities'}`,
        };
        setRightPanelContext(newCtx);
        pushHistory(newCtx, entityInfo.title || 'Components List');
      } else if (
        entityInfo.entityType === 'dataSourceInstance' ||
        entityInfo.entityType === 'agentInstance' ||
        entityInfo.entityType === 'userAppInstance' ||
        entityInfo.entityType === ('data_source' as any) ||
        entityInfo.entityType === ('agent' as any) ||
        entityInfo.entityType === ('user_app' as any) ||
        entityInfo.type === 'single'
      ) {
        let fullData = entityInfo.data || {};
        let defName = entityInfo.name || fullData.defName || fullData.name;

        let normalizedType: EntityType = entityInfo.entityType || EntityType.dataSourceInstance;
        if (entityInfo.entityType === ('data_source' as any)) normalizedType = EntityType.dataSourceInstance;
        if (entityInfo.entityType === ('agent' as any)) normalizedType = EntityType.agentInstance;
        if (entityInfo.entityType === ('user_app' as any)) normalizedType = EntityType.userAppInstance;

        const stepId = entityInfo.id || fullData.id || 'step_1';
        const defId =
          fullData.defId ||
          fullData.dataSourceDefId ||
          fullData.data_source_def_id ||
          fullData.agentDefId ||
          fullData.agent_def_id ||
          fullData.userAppDefId ||
          fullData.user_app_def_id ||
          fullData.pipelineDefId ||
          fullData.pipeline_def_id ||
          entityInfo.id;
        const itemName = entityInfo.name || fullData.name || defName || stepId;

        setPipelineExecutionState(undefined);
        setPipelineId(null);
        setSelectedStep(null);

        const newCtx: RightPanelContext = {
          type: 'single',
          entityType: normalizedType,
          entityId: stepId,
          entityDefId: defId,
          entityName: itemName,
          entityData: fullData,
          entitySummary: `${normalizedType === 'dataSourceInstance' ? 'Data Source' : normalizedType === 'agentInstance' ? 'Agent' : 'User App'} "${itemName}" (ID: ${stepId})`,
          previousContext: entityInfo.previousContext,
        };
        setRightPanelContext(newCtx);
        pushHistory(
          newCtx,
          `${normalizedType === 'dataSourceInstance' ? 'Data Source' : normalizedType === 'agentInstance' ? 'Agent' : 'User App'}: ${itemName}`,
        );
      }
    },
    [handlePipelineSelect, pushHistory],
  );

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    chatRef.current?.startNewConversation();
    const emptyCtx: RightPanelContext = { type: 'empty' };
    setRightPanelContext(emptyCtx);
    setPipelineExecutionState(undefined);
    setPipelineId(null);
    setSelectedStep(null);
    setHistoryList([
      {
        id: 'init',
        title: 'Workspace Suggestions',
        type: 'empty',
        timestamp: new Date(),
        context: emptyCtx,
      },
    ]);
    setCurrentHistoryIndex(0);
  }, []);

  const handleSelectSuggestion = useCallback(
    async (item: SuggestionItem) => {
      if (item.actionKey && chatRef.current?.executeDirectAction) {
        if (item.actionKey === 'discover_components') {
          await chatRef.current.executeDirectAction(item.label, async (assistantMsgId) => {
            const [dsInstances, agentInstances, appInstances] = await Promise.all([
              apiContext.getDataSourceInstances(false, false),
              apiContext.getAgentInstances(false, false),
              apiContext.getUserAppInstances(false, false),
            ]);
            const allInstances = [
              ...(dsInstances || []).map((d) => ({ ...d, entityType: EntityType.dataSourceInstance })),
              ...(agentInstances || []).map((d) => ({ ...d, entityType: EntityType.agentInstance })),
              ...(appInstances || []).map((d) => ({ ...d, entityType: EntityType.userAppInstance })),
            ];
            handleEntityDetected({
              type: 'entity_list',
              entityType: EntityType.pipelineInstance,
              title: 'Available Components',
              items: allInstances,
            });
          });
        } else if (item.actionKey === 'list_datasources') {
          await chatRef.current.executeDirectAction(item.label, async (assistantMsgId) => {
            const dsInstances = await apiContext.getDataSourceInstances(false, false);
            const items = (dsInstances || []).map((d) => ({ ...d, entityType: EntityType.dataSourceInstance }));
            handleEntityDetected({
              type: 'entity_list',
              entityType: EntityType.dataSourceInstance,
              title: 'Data Sources',
              items,
            });
          });
        } else if (item.actionKey === 'list_agents') {
          await chatRef.current.executeDirectAction(item.label, async (assistantMsgId) => {
            const agentInstances = await apiContext.getAgentInstances(false, false);
            const items = (agentInstances || []).map((d) => ({ ...d, entityType: EntityType.agentInstance }));
            handleEntityDetected({
              type: 'entity_list',
              entityType: EntityType.agentInstance,
              title: 'Agents',
              items,
            });
          });
        } else if (item.actionKey === 'list_user_apps') {
          await chatRef.current.executeDirectAction(item.label, async (assistantMsgId) => {
            const appInstances = await apiContext.getUserAppInstances(false, false);
            const items = (appInstances || []).map((d) => ({ ...d, entityType: EntityType.userAppInstance }));
            handleEntityDetected({
              type: 'entity_list',
              entityType: EntityType.userAppInstance,
              title: 'User Applications',
              items,
            });
          });
        } else if (item.actionKey === 'list_pipelines') {
          await chatRef.current.executeDirectAction(item.label, async (assistantMsgId) => {
            const pipelines = await apiContext.getPipelineInstances(undefined, undefined, false);
            const items = (pipelines || []).map((d) => ({ ...d, entityType: EntityType.pipelineInstance }));
            handleEntityDetected({
              type: 'entity_list',
              entityType: EntityType.pipelineInstance,
              title: 'Pipelines',
              items,
            });
          });
        } else if (item.actionKey === 'list_blueprints') {
          await chatRef.current.executeDirectAction(item.label, async (assistantMsgId) => {
            const blueprints = await apiContext.getPipelineBlueprints();
            const items = (blueprints || []).map((d) => ({ ...d, entityType: EntityType.pipelineBlueprint }));
            handleEntityDetected({
              type: 'entity_list',
              entityType: EntityType.pipelineBlueprint,
              title: 'Pipeline Blueprints',
              items,
            });
          });
        } else if (item.actionKey === 'pipeline_status') {
          await chatRef.current.executeDirectAction(item.label, async (assistantMsgId) => {
            const pipelines = await apiContext.getPipelineInstances(undefined, undefined, false);
            if (pipelines && pipelines.length > 0) {
              await handlePipelineSelect(pipelines[0].id);
            } else {
              handleEntityDetected({
                type: 'entity_list',
                entityType: EntityType.pipelineInstance,
                title: 'Pipelines',
                items: [],
              });
            }
          });
        }
      } else {
        chatRef.current?.sendPrompt(item.prompt);
      }
    },
    [apiContext, handleEntityDetected, handlePipelineSelect],
  );

  const handleSelectPrompt = useCallback((promptText: string) => {
    chatRef.current?.sendPrompt(promptText);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 130px)',
        bgcolor: '#f1f5f9',
        overflow: 'hidden',
        p: 1,
        gap: 1,
      }}
    >
      {/* Page Header */}
      <ChantAssistantHeader
        pipelineExecutionState={pipelineExecutionState}
        handleNewChat={handleNewChat}
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
      />

      {/* Split Panel */}
      <Box sx={{ display: 'flex', flexGrow: 1, gap: 2, overflow: 'hidden' }}>
        {/* Left Panel – Chat (40%) */}
        <Box
          sx={{
            width: '40%',
            minWidth: 340,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <AssistantChat
            ref={chatRef}
            onEntityDetected={handleEntityDetected}
            rightPanelContext={rightPanelContext}
            pipelineExecutionState={pipelineExecutionState}
            selectedStep={selectedStep}
            suppressEvents={suppressEvents}
            onExecuteStep={handleExecuteStep}
            initialConversationId={activeConversationId}
            onConversationSaved={(newConvId) => {
              if (newConvId) {
                setActiveConversationId(newConvId);
              }
              refreshConversations();
            }}
            onConversationDeleted={(deletedId) => {
              setConversations((prev) => prev.filter((c) => c.id !== deletedId));
              if (activeConversationId === deletedId) {
                setActiveConversationId(null);
              }
            }}
          />
        </Box>

        {/* Right Panel – Entity Context (60%) */}
        <EntityContextPanel
          rightPanelContext={rightPanelContext}
          pipelineExecutionState={pipelineExecutionState}
          selectedStep={selectedStep}
          executingStepId={executingStepId}
          stepExecutionResponse={stepExecutionResponse}
          suppressEvents={suppressEvents}
          onToggleSuppressEvents={setSuppressEvents}
          onSelectStep={setSelectedStep}
          onExecuteStep={handleExecuteStep}
          onRestartFromPoint={handleRestartFromPoint}
          onClearStepExecutionResponse={() => {
            setStepExecutionResponse(null);
            setExecutingStepId(null);
          }}
          onChange={reloadPipelineExecutionPlan}
          onPipelineSelect={handlePipelineSelect}
          onEntitySelect={handleEntityDetected}
          onResetContext={() => setRightPanelContext({ type: 'empty' })}
          onSelectSuggestion={handleSelectSuggestion}
          onSelectPrompt={handleSelectPrompt}
          onHistoryBack={handleHistoryBack}
          onHistoryForward={handleHistoryForward}
          historyList={historyList}
          currentHistoryIndex={currentHistoryIndex}
          onHistoryJump={handleHistoryJump}
        />
      </Box>
    </Box>
  );
};

export default ChatAssistantPage;
